/**
 * «DOVE VALE» — la logica, senza il disegno (task BE 3579, in esercizio sul dev dal 02-11-2026).
 *
 * Ogni configurazione «di gruppo» (`IHasScope`: causali, chiusure, tipi di assenza, politiche…) ha
 * due campi sul filo:
 * · `scope` — `tenant` (solo l'azienda che la scrive) o `group` (scende alle aziende sotto);
 *   sulla policy dello smart working lo stesso valore si chiama `holdingScope`;
 * · `scopeTenantIds` — con `group`, le aziende a cui scende. **Vuoto = tutte** quelle che la riga
 *   raggiunge (il comportamento di prima); pieno = solo quelle **e il loro ramo**. Con `tenant` il
 *   server lo ignora. L'azienda che scrive la riga la vede sempre, senza elencarla.
 *
 * A schermo sono tre scelte, non due campi: «Solo questa azienda», «Tutto il gruppo», «Solo alcune
 * aziende». Nel form la terza si distingue dalla seconda così: `ids === null` è «tutto il gruppo»,
 * un array — anche vuoto, appena scelto — è «alcune». Sul filo l'array vuoto torna a voler dire
 * «tutte», ed è per questo che «alcune» senza nessuna azienda **non si salva** (`whereIssue`): si
 * manderebbe il contrario di quel che si legge a schermo.
 *
 * ⚠️ La prova lato server degli id fuori perimetro (`422 SCOPE_TENANT_NOT_REACHABLE`) al 02-11-2026
 * **non è attiva**: un id che la riga non raggiunge viene ignorato, senza errore. Quindi qui si
 * offrono solo le aziende sotto quella corrente, e quelle che non ci sono più si dicono.
 */
import { parentIdOf, type TenantNode } from '@/features/tenants/groupRoot';

export type WhereMode = 'tenant' | 'group' | 'some';

/** Il nome dell'elenco sul filo: è lo stesso su tutte e venti le risorse. */
export const SCOPE_TENANT_IDS = 'scopeTenantIds';

/** Il valore del form: l'ambito com'è sul filo, e l'elenco (`null` = tutto il gruppo). */
export interface WhereValue {
  scope: 'tenant' | 'group';
  ids: string[] | null;
}

export const WHERE_MODE_LABEL: Record<WhereMode, string> = {
  tenant: 'Solo questa azienda',
  group: 'Tutto il gruppo',
  some: 'Solo alcune aziende',
};

const low = (s: unknown) => String(s ?? '').toLowerCase();

/** Gli id senza doppioni, tenendo la prima scrittura (il server ha già mostrato maiuscole e minuscole). */
function uniqueIds(ids: readonly unknown[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of ids) {
    const id = String(raw ?? '').trim();
    if (!id || seen.has(low(id))) continue;
    seen.add(low(id));
    out.push(id);
  }
  return out;
}

/** Dalla riga letta al valore del form. */
export function whereFromRow(scope: unknown, rawIds: unknown): WhereValue {
  if (scope !== 'group') return { scope: 'tenant', ids: null };
  const ids = Array.isArray(rawIds) ? uniqueIds(rawIds) : [];
  return { scope: 'group', ids: ids.length ? ids : null };
}

/** Dal valore del form ai due attributi da scrivere. Con «solo questa azienda» l'elenco si svuota. */
export function whereToWire(v: WhereValue): { scope: 'tenant' | 'group'; scopeTenantIds: string[] } {
  if (v.scope !== 'group') return { scope: 'tenant', scopeTenantIds: [] };
  return { scope: 'group', scopeTenantIds: v.ids ? uniqueIds(v.ids) : [] };
}

export function whereModeOf(v: WhereValue): WhereMode {
  if (v.scope !== 'group') return 'tenant';
  return v.ids === null ? 'group' : 'some';
}

/**
 * Il valore dopo aver scelto un modo. L'elenco si porta dietro: chi passa da «alcune» a «tutto il
 * gruppo» e torna ritrova le aziende che aveva spuntato, invece di ricominciare.
 */
export function chooseMode(mode: WhereMode, remembered: readonly string[] | null): WhereValue {
  if (mode === 'tenant') return { scope: 'tenant', ids: null };
  if (mode === 'group') return { scope: 'group', ids: null };
  return { scope: 'group', ids: remembered ? [...remembered] : [] };
}

/** Cosa impedisce di salvare; `null` se si può. */
export function whereIssue(v: WhereValue): string | null {
  if (v.scope === 'group' && v.ids !== null && v.ids.length === 0) {
    return 'In «Dove vale» scegli almeno un’azienda, oppure «Tutto il gruppo».';
  }
  return null;
}

/* ─────────────────────── l'albero ─────────────────────── */

/** Un'azienda del ramo, con la profondità sotto quella corrente (1 = figlia diretta). */
export interface BranchOption {
  id: string;
  name: string;
  depth: number;
  parentId: string | null;
}

/**
 * Le aziende sotto quella corrente, in ordine d'albero (ogni madre prima delle sue figlie, sorelle
 * per nome). Sono le sole che una riga di gruppo raggiunge, quindi le sole da offrire.
 */
export function branchOptions(currentTenantId: string | null | undefined, nodes: readonly TenantNode[]): BranchOption[] {
  if (!currentTenantId) return [];
  const children = new Map<string, TenantNode[]>();
  for (const n of nodes) {
    const p = low(parentIdOf(n));
    if (!p) continue;
    const list = children.get(p) ?? [];
    list.push(n);
    children.set(p, list);
  }
  const out: BranchOption[] = [];
  const seen = new Set<string>([low(currentTenantId)]);
  const visit = (parent: string, depth: number) => {
    const kids = [...(children.get(parent) ?? [])].sort((a, b) => String(a.name ?? '').localeCompare(String(b.name ?? '')));
    for (const k of kids) {
      const id = low(k.id);
      if (seen.has(id)) continue;
      seen.add(id);
      out.push({ id: String(k.id), name: String(k.name ?? k.id), depth, parentId: parentIdOf(k) });
      visit(id, depth + 1);
    }
  };
  visit(low(currentTenantId), 1);
  return out;
}

/** L'azienda scelta che già comprende questa (una sua antenata nell'elenco), o `null`. */
export function coveringChoice(id: string, selected: readonly string[], branch: readonly BranchOption[]): string | null {
  const byId = new Map(branch.map((b) => [low(b.id), b]));
  const chosen = new Set(selected.map(low));
  let cur = byId.get(low(id));
  const guard = new Set<string>();
  while (cur?.parentId && !guard.has(low(cur.id))) {
    guard.add(low(cur.id));
    const parent = low(cur.parentId);
    if (chosen.has(parent)) return byId.get(parent)?.id ?? cur.parentId;
    cur = byId.get(parent);
  }
  return null;
}

/**
 * L'elenco come va tenuto: senza doppioni e senza le aziende già comprese nel ramo di un'altra
 * scelta (spuntare un'azienda porta con sé le sue controllate, regola 1 del 3579). Gli id fuori dal
 * ramo restano: si dicono a parte (`outsideBranch`), non si buttano in silenzio.
 */
export function normalizeSelection(ids: readonly string[], branch: readonly BranchOption[]): string[] {
  const unique = uniqueIds(ids);
  return unique.filter((id) => !coveringChoice(id, unique, branch));
}

/** Gli id dell'elenco che non sono (più) sotto l'azienda corrente: la riga non li raggiunge. */
export function outsideBranch(ids: readonly string[], branch: readonly BranchOption[]): string[] {
  const known = new Set(branch.map((b) => low(b.id)));
  return ids.filter((id) => !known.has(low(id)));
}

/**
 * La colonna «Dove vale» di un elenco a schema (`CrudColumn`, scritta per forma per non importare
 * il disegno qui): conta le aziende, perché i nomi non stanno sulla riga.
 */
export function whereCol(key = 'scope', label = 'Dove vale'): { key: string; label: string; render: (row: unknown) => string } {
  return {
    key,
    label,
    render: (row) => {
      const r = (row ?? {}) as Record<string, unknown>;
      return whereSummary(r[key], r[SCOPE_TENANT_IDS], () => null);
    },
  };
}

/**
 * La frase per l'elenco: «Solo questa azienda», «Tutto il gruppo», «Solo: Alfa, Beta» — oltre tre
 * nomi, i primi tre e quante altre. Un id senza nome (azienda che chi guarda non vede) si conta.
 */
export function whereSummary(scope: unknown, rawIds: unknown, nameOf: (id: string) => string | null | undefined): string {
  const v = whereFromRow(scope, rawIds);
  if (v.scope !== 'group') return WHERE_MODE_LABEL.tenant;
  if (!v.ids) return WHERE_MODE_LABEL.group;
  const names = v.ids.map((id) => nameOf(id)).filter((n): n is string => Boolean(n));
  const unnamed = v.ids.length - names.length;
  const shown = names.slice(0, 3);
  const rest = names.length - shown.length + unnamed;
  if (!shown.length) return `Solo ${v.ids.length === 1 ? 'un’azienda' : `${v.ids.length} aziende`} del gruppo`;
  return `Solo: ${shown.join(', ')}${rest > 0 ? ` e ${rest === 1 ? 'un’altra' : `altre ${rest}`}` : ''}`;
}
